const CUSTOM_ACCENT_KEY = 'customThemeAccent';
const DEFAULT_ACCENT = '#4F46E5';

const normalizeHex = (value: string) => {
  const hex = value.trim().replace('#', '');
  if (/^[0-9a-fA-F]{3}$/.test(hex)) {
    return `#${hex
      .split('')
      .map(character => character + character)
      .join('')}`.toUpperCase();
  }
  return /^[0-9a-fA-F]{6}$/.test(hex)
    ? `#${hex.toUpperCase()}`
    : null;
};

const toRgb = (hex: string) => {
  const normalized = normalizeHex(hex) ?? DEFAULT_ACCENT;
  return {
    r: parseInt(normalized.slice(1, 3), 16),
    g: parseInt(normalized.slice(3, 5), 16),
    b: parseInt(normalized.slice(5, 7), 16)
  };
};

const mix = (hex: string, target: '#000000' | '#FFFFFF', amount: number) => {
  const source = toRgb(hex);
  const destination = toRgb(target);
  const channel = (from: number, to: number) =>
    Math.round(from + (to - from) * amount)
      .toString(16)
      .padStart(2, '0');
  return `#${channel(source.r, destination.r)}${channel(source.g, destination.g)}${channel(source.b, destination.b)}`.toUpperCase();
};

const readableForeground = (hex: string) => {
  const { r, g, b } = toRgb(hex);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.62 ? '#101828' : '#FFFFFF';
};

export const getCustomAccent = () =>
  normalizeHex(localStorage.getItem(CUSTOM_ACCENT_KEY) ?? '') ?? DEFAULT_ACCENT;

export const applyCustomAccent = (value: string, mode: string) => {
  const accent = normalizeHex(value);
  if (!accent) return false;

  const root = document.documentElement;
  const darkMode = mode === 'dark';
  const sidebar = mix(accent, '#000000', darkMode ? 0.64 : 0.42);
  const softSurface = mix(accent, darkMode ? '#000000' : '#FFFFFF', darkMode ? 0.72 : 0.91);
  const softBorder = mix(accent, darkMode ? '#FFFFFF' : '#FFFFFF', darkMode ? 0.2 : 0.72);

  root.style.setProperty('--color-brand-primary', accent);
  root.style.setProperty('--color-text-link', accent);
  root.style.setProperty('--color-sidebar-bg', sidebar);
  root.style.setProperty('--color-sidebar-text', readableForeground(sidebar));
  root.style.setProperty('--color-sidebar-icon-color', readableForeground(sidebar));
  root.style.setProperty('--color-sidebar-active-text', readableForeground(sidebar));
  root.style.setProperty(
    '--color-sidebar-icon-hover-bg',
    `rgba(${toRgb(accent).r}, ${toRgb(accent).g}, ${toRgb(accent).b}, 0.22)`
  );
  root.style.setProperty('--color-header-highlight-bg', softSurface);
  root.style.setProperty('--color-header-highlight-text', darkMode ? mix(accent, '#FFFFFF', 0.25) : mix(accent, '#000000', 0.2));
  root.style.setProperty('--color-step-badge-bg', accent);
  root.style.setProperty('--color-step-badge-text', readableForeground(accent));
  root.style.setProperty('--color-card-icon-bg', accent);
  root.style.setProperty('--color-card-icon-text', readableForeground(accent));
  root.style.setProperty('--color-card-value-text', darkMode ? mix(accent, '#FFFFFF', 0.24) : mix(accent, '#000000', 0.08));
  root.style.setProperty('--color-border-light-blue', softBorder);
  root.style.setProperty(
    '--gradient-surface-blue',
    darkMode
      ? `linear-gradient(145deg, ${mix(accent, '#000000', 0.78)} 0%, #17191e 100%)`
      : `linear-gradient(145deg, #FFFFFF 8%, ${softSurface} 100%)`
  );
  root.style.setProperty(
    '--shadow-card',
    `0 1px 3px rgba(16, 24, 40, 0.06), 0 8px 24px rgba(${toRgb(accent).r}, ${toRgb(accent).g}, ${toRgb(accent).b}, 0.10)`
  );
  localStorage.setItem(CUSTOM_ACCENT_KEY, accent);
  return true;
};

export { DEFAULT_ACCENT, normalizeHex };
